import _ from 'lodash'
import type { DiceContext, SingleDiceResult, TextPart } from './diceEval'
import { roll } from './diceRoll'

export type DiceFnResult = { value: number; average: number }
export type DiceFn = (args: SingleDiceResult[], ctx: DiceContext) => SingleDiceResult

const diceFunctions: Record<string, DiceFn> = {
  // the `dice` function is transparent, ie it does not display as a function
  // it is very useful to integrate mods with dice rolls, such as :=dice(prof, 6):
  dice(args: SingleDiceResult[], ctx: DiceContext) {
    if (args.length !== 2) return ERROR

    const amount = args[0].rolled || 1
    const sides = args[1].rolled || 1

    // TODO: it would be nice to able to pass mods here
    // passing them through a missing variable name does not quite work, since mods needs to be able to accept number args, and variables are only [a-z]
    return roll(amount, sides, [], ctx)
  },
  max(args: SingleDiceResult[]) {
    if (args.length === 0) return ERROR
    const value = _.max(args.map((a) => a.rolled))!
    return makeFunctionResult('max', value, true, args)
  },
  min(args: SingleDiceResult[]) {
    if (args.length === 0) return ERROR
    const value = _.min(args.map((a) => a.rolled))!
    return makeFunctionResult('min', value, true, args)
  },
  abs(args: SingleDiceResult[]) {
    if (args.length !== 1) return ERROR
    const value = Math.abs(args[0].rolled)
    return makeFunctionResult('abs', value, true, args)
  },
  // `pick` evaluates its first argument, and then returns the argument with that index (or 0 if that argument is not available)
  pick(args: SingleDiceResult[]) {
    if (args.length === 0) return ERROR
    const index = args[0].rolled
    const value = args[index]?.rolled ?? 0
    return makeFunctionResult('pick', value, true, args)
  },
}

const ERROR: SingleDiceResult = {
  rolled: 0,
  average: 0,
  result: [{ type: 'text', text: 'fnError' }],
  resultSimple: [{ type: 'text', text: 'fnError' }],
  constant: true,
  expression: `(function error)`,
  simplified: `(function error)`,
}

const mkText = (text: string): TextPart => ({ type: 'text', text })

const makeFunctionResult = (name: string, value: number, pure: boolean, args: SingleDiceResult[]) => {
  // there is no easy way to simplify the resulting expressions
  const resultArgs = args.flatMap((a, i) => (i === 0 ? a.result : [mkText(','), ...a.result]))
  const resultSimpleArgs = args.flatMap((a, i) => (i === 0 ? a.resultSimple : [mkText(','), ...a.result]))

  return {
    rolled: value,
    average: value,
    result: [mkText(`${name}(`), ...resultArgs, mkText(')')],
    resultSimple: [mkText(`${name}(`), ...resultSimpleArgs, mkText(')')],
    // pure functions are constant if all its arguments are
    constant: pure ? args.every((a) => a.constant) : false,
    expression: `${name}(${args.map((arg) => arg.expression).join(', ')})`,
    simplified: `${name}(${args.map((arg) => arg.simplified).join(', ')})`,
  }
}

export default diceFunctions
